import React from 'react';
import { Controller, FieldArrayWithId, useFieldArray, useFormContext } from 'react-hook-form';

import {
  Table,
  Paper,
  TableBody,
  TableContainer,
  Autocomplete,
  IconButton,
  TextField,
  Box,
  Button
} from '@mui/material';
import { Add, DeleteOutlineRounded } from '@mui/icons-material';

import { StyledTableCell, StyledTableRow } from '../../styled-components';
import _ from 'lodash';
import { Site } from 'constants/sites.constants';
import { useLocation } from 'react-router-dom';
import { ISitePermission, UserRole } from 'constants/user.constants';
import { useAppSelector } from 'hooks/redux.hooks';
import { Status } from 'constants/global.constans';

interface ISitePermissionTable {
  isEditMode: boolean;
  sitePermissions?: any;
  role?: UserRole;
}

type FormValues = {
  sitePermissions: Array<ISitePermission>;
};

export const SitePermissionTable: React.FC<ISitePermissionTable> = ({ isEditMode, sitePermissions, role }) => {
  const { control, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray<FormValues, 'sitePermissions'>({
    name: 'sitePermissions'
  });
  const { sites } = useAppSelector((state) => state.user);

  const location = useLocation();
  const edit = location?.pathname?.includes('new') || isEditMode;

  const handleRemove = (id: string) => {
    remove(fields.findIndex((item) => item.id === id));
  };

  const getUnChosenOptions = () => {
    const chosenOptions = getValues('sitePermissions')?.map((item: ISitePermission) => item.id);
    return _.filter(sitePermissions, (item: Site) => !chosenOptions?.includes(item.id));
  };

  const renderOptions = (formField: FieldArrayWithId<FormValues>, index: number, active = true) => {
    return (
      <Controller
        name={`sitePermissions.${index}.id`}
        control={control}
        rules={{
          required: true
        }}
        render={({ field: { onChange } }) => {
          return (
            <Autocomplete
              onChange={(e, item: Site | null) => {
                onChange(item?.id);
              }}
              disabled={!edit}
              getOptionLabel={(option) => option.name as string}
              autoHighlight
              options={getUnChosenOptions()}
              getOptionDisabled={(option) => !option.active as boolean}
              sx={{ width: '100%' }}
              renderInput={(params) => {
                //this contains input value after change
                const inputValue = params?.inputProps?.value || '';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {edit && (
                      <IconButton
                        sx={{
                          marginRight: '10px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          handleRemove(formField.id);
                        }}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    )}
                    <TextField
                      sx={{
                        '& fieldset': { border: !edit ? 'none' : null }
                      }}
                      //if we have input value we dont want to show label
                      label={
                        !inputValue && formField?.name ? formField.name + (active ? '' : ` (${Status.Inactive})`) : ''
                      }
                      inputProps={{ ...params.inputProps, 'data-testid': 'add-new' }}
                      InputLabelProps={params.InputLabelProps}
                      InputProps={params.InputProps}
                      disabled={params.disabled}
                      fullWidth={params.fullWidth}
                      id={params.id}
                      size={params.size}
                    />
                  </Box>
                );
              }}
            />
          );
        }}
      />
    );
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ maxWidth: 768 }}>
        <Table aria-label='customized table'>
          <TableBody>
            {_.map(fields, (field, index) => {
              const active = sites?.find((site) => site?.name === field?.name)?.active ?? true;
              return (
                <StyledTableRow key={field.id}>
                  <StyledTableCell
                    sx={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    variant='body'
                    align='left'
                  >
                    {renderOptions(field, index, active)}
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
            {edit && (
              <StyledTableRow>
                <StyledTableCell>
                  <Button
                    sx={{ m: 2 }}
                    color='secondary'
                    variant='text'
                    startIcon={<Add />}
                    onClick={() => {
                      append({ id: 0, role: role || undefined });
                    }}
                  >
                    Add New Site
                  </Button>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
